import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { stripHtml } from '../../utils/html';
import { Button, Card, Popover, Space, Tag, Typography } from 'antd';
import {
  CalendarOutlined,
  CheckCircleOutlined,
  EyeOutlined,
  EnvironmentOutlined,
  FileSearchOutlined,
  MobileOutlined,
  ShareAltOutlined,
  UserOutlined,
} from '@ant-design/icons';
import dayjs from 'dayjs';
import { PublicSurveyListItem, PublicSurveyStatus } from '../../types/publicSurvey.types';
import ResponseAuditModal from './ResponseAuditModal';
import { ShareButtons } from '../common/ShareButtons';
import { useAuth } from '../../hooks/useAuth';
import { useGuestSession } from '../../services/useGuestSession';

const { Text, Paragraph } = Typography;

const BRAND_INDIGO = '#2E2B81';
const BRAND_GREEN = '#00984A';

interface PublicSurveyCardProps {
  item: PublicSurveyListItem;
  status?: PublicSurveyStatus;
  onParticipate?: (surveyId: string) => void;
  shareUrl?: string;
}

const SurveySharePopover = ({ shareUrl, surveyName, surveyHash }: { shareUrl: string; surveyName: string; surveyHash?: string }) => {
  // shareUrl já carrega o ?ref= do indicador (ver buildSurveyShareUrl) — o link
  // encurtado precisa do mesmo código, senão a indicação se perde na criação do ShortLink.
  const referralCode = new URL(shareUrl, window.location.origin).searchParams.get('ref') ?? undefined;

  return (
    <ShareButtons
      title={`Pesquisa: ${surveyName} - PePoll`}
      description="Participe desta pesquisa na PePoll"
      url={shareUrl}
      surveyHash={surveyHash}
      referralCode={referralCode}
      size="small"
      channels={['whatsapp', 'facebook', 'copy']}
      spacing="small"
    />
  );
};

export const PublicSurveyCard: React.FC<PublicSurveyCardProps> = ({ item, status, onParticipate, shareUrl }) => {
  const [auditModalOpen, setAuditModalOpen] = useState(false);
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();
  const guestSession = useGuestSession();
  const isActive = status === 'ACTIVE';
  const isPeriodExpired = isActive && dayjs().isAfter(dayjs(item.endDate));
  const canParticipate = item.canParticipate !== false;
  const hasResponded = isActive && !canParticipate;
  const canOpenAudit = hasResponded && Boolean(item.responseHash);
  const accentColor = isActive ? BRAND_GREEN : '#bfbfbf';
  const isBlocked = isPeriodExpired || hasResponded;
  // Convidado com sessão de telefone já verificada: atalho direto para o questionário,
  // sem passar de novo pela landing page.
  const hasActiveGuestSession = !isAuthenticated && item.allowGuestParticipation && guestSession.isActive && !isBlocked;

  const handleGuestContinue = () => {
    navigate(`/surveys/${item.surveyId}/guest-take`);
  };

  const handleParticipateClick = () => {
    if (hasActiveGuestSession) {
      handleGuestContinue();
      return;
    }
    if (isAuthenticated) {
      onParticipate?.(item.surveyId);
      return;
    }
    // Deslogado: sempre manda para a landing page da pesquisa, que apresenta
    // as opções de Entrar / Criar Conta / Participar com código por SMS.
    navigate(`/public/surveys/${item.publicAuditHash}`);
  };

  return (
    <Card
      style={{
        width: '100%',
        height: '100%',
        borderLeft: `4px solid ${accentColor}`,
        borderRadius: 8,
      }}
      styles={{ body: { padding: '20px 20px 16px' } }}
    >
      <Space direction="vertical" size={12} style={{ width: '100%', height: '100%' }}>
        {/* Título + badge de status */}
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'flex-start',
            gap: 8,
          }}
        >
          <Text strong style={{ flex: 1, color: BRAND_INDIGO, fontSize: 14, lineHeight: '1.4' }}>
            {item.name}
          </Text>
          <Space size={6} style={{ flexShrink: 0 }}>
            {hasResponded && (
              <Tag color="success" icon={<CheckCircleOutlined />} style={{ marginInlineEnd: 0 }}>
                Respondida
              </Tag>
            )}
            {isActive && shareUrl && (
              <Popover
                trigger="click"
                placement="bottomRight"
                content={<SurveySharePopover shareUrl={shareUrl} surveyName={item.name} surveyHash={item.publicAuditHash} />}
              >
                <Button size="small" type="text" icon={<ShareAltOutlined />} aria-label="Compartilhar pesquisa" />
              </Popover>
            )}
          </Space>
        </div>

        {item.tags && item.tags.length > 0 && (
          <Space size={[8, 8]} wrap>
            {item.tags.map((tag) => (
              <Tag key={tag.id} color="blue" style={{ marginInlineEnd: 0 }}>
                {tag.name}
              </Tag>
            ))}
          </Space>
        )}

        {/* Datas + Abrangência + Contratante */}
        <Space size={16} wrap>
          <Text
            type={isPeriodExpired ? 'danger' : 'secondary'}
            style={{ fontSize: 12 }}
          >
            <CalendarOutlined style={{ marginRight: 5 }} />
            {dayjs(item.startDate).format('DD/MM/YYYY')} → {dayjs(item.endDate).format('DD/MM/YYYY')}
            {isPeriodExpired && ' · Período encerrado'}
          </Text>
          <Text type="secondary" style={{ fontSize: 12 }}>
            <EnvironmentOutlined style={{ marginRight: 4 }} />
            Abrangência: {item.geographicScope}
          </Text>
        </Space>
        
        <Space size={16} wrap>
          <Text type="secondary" style={{ fontSize: 12 }}>
            <UserOutlined style={{ marginRight: 4 }} />
            Contratante: {item.contractor.name}
          </Text>
          <Text type="secondary" style={{ fontSize: 12 }}>
            <EyeOutlined style={{ marginRight: 4 }} />
            Visibilidade: {item.visibility.disclosureRuleText}
            {item.visibility.disclosureDate ? ` (${dayjs(item.visibility.disclosureDate).format('DD/MM/YYYY')})` : ''}
          </Text>
        </Space>



        {/* Descrição com ellipsis */}
        <Paragraph
          ellipsis={{ rows: 2, tooltip: stripHtml(item.description) }}
          style={{ marginBottom: 0, fontSize: 13, color: 'rgba(0,0,0,0.60)' }}
        >
          {stripHtml(item.description)}
        </Paragraph>

        {/* Ações */}
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', paddingTop: 4 }}>
          <Button size="small" href={item.trailUrl}>
            Ver trilha completa
          </Button>
          {canOpenAudit && (
            <Button
              size="small"
              icon={<FileSearchOutlined />}
              onClick={() => setAuditModalOpen(true)}
            >
              Ver Auditoria
            </Button>
          )}
          {isActive && onParticipate && (
            <Button
              size="small"
              type="primary"
              icon={hasActiveGuestSession ? <MobileOutlined /> : undefined}
              disabled={isBlocked}
              onClick={!isBlocked ? handleParticipateClick : undefined}
            >
              {hasActiveGuestSession ? 'Continuar com número verificado' : 'Participar'}
            </Button>
          )}
        </div>

        {canOpenAudit && item.responseHash && (
          <ResponseAuditModal
            open={auditModalOpen}
            onClose={() => setAuditModalOpen(false)}
            responseHash={item.responseHash}
            surveyName={item.name}
            surveyPublicAuditHash={item.publicAuditHash}
          />
        )}
      </Space>
    </Card>
  );
};
